// Runtime Excel-based CMS.
// Site loads /data/dealers.xlsx and /data/products.xlsx on demand.
// To update: edit the Excel file, replace it in the /data/ folder via cPanel — done.
import * as XLSX from "xlsx";
import DEALERS_FALLBACK from "@/data/dealers.json";

const CONTACT_EMAIL = "info@jaininteriorsolutions.in";
const FORMSUBMIT_URL = `https://formsubmit.co/ajax/${encodeURIComponent(CONTACT_EMAIL)}`;

// --- helpers ---
const truthy = (v) => {
  const s = String(v ?? "").trim().toLowerCase();
  return s === "yes" || s === "y" || s === "true" || s === "1";
};

async function loadSheet(path) {
  const res = await fetch(path, { cache: "no-store" });
  if (!res.ok) throw new Error(`fetch ${path} -> ${res.status}`);
  const buf = await res.arrayBuffer();
  const wb = XLSX.read(buf, { type: "array" });
  const first = wb.SheetNames[0];
  return XLSX.utils.sheet_to_json(wb.Sheets[first], { defval: "" });
}

// --- Dealers ---
let dealersCache = null;
async function getDealers() {
  if (dealersCache) return dealersCache;
  try {
    const rows = await loadSheet("/data/dealers.xlsx");
    dealersCache = rows.map((r, i) => ({
      id: String(i + 1),
      company: r.company || r.Company || "",
      name: r.name || r.Name || r.company || "",
      type: r.type || r.Type || "Dealer",
      city: r.city || r.City || "",
      state: r.state || r.State || "",
      phone: r.phone || r.Phone || "",
      email: r.email || r.Email || "",
      address: r.address || r.Address || "",
      specialization: r.type || r.Type || "Dealer",
      demo: truthy(r.demo ?? r.Demo),
      purchase: truthy(r.purchase ?? r.Purchase),
      installation: truthy(r.installation ?? r.Installation),
    }));
  } catch (e) {
    console.warn("Loading dealers.xlsx failed, using bundled fallback:", e);
    dealersCache = DEALERS_FALLBACK;
  }
  return dealersCache;
}

export const fetchDealers = async ({ state, city } = {}) => {
  let list = [...(await getDealers())];
  if (state && state.toLowerCase() !== "all") list = list.filter((d) => d.state === state);
  if (city && city.toLowerCase() !== "all") list = list.filter((d) => d.city === city);
  list.sort((a, b) => (a.state || "").localeCompare(b.state || ""));
  return list;
};

export const fetchLocations = async () => {
  const dealers = await getDealers();
  const map = {};
  dealers.forEach((d) => {
    if (!d.state) return;
    if (!map[d.state]) map[d.state] = new Set();
    if (d.city) map[d.state].add(d.city);
  });
  const states = Object.keys(map).sort();
  const locations = states.map((s) => ({ state: s, cities: [...map[s]].sort() }));
  return { states, locations, total: dealers.length };
};

export const fetchStats = async () => ({
  cities_served: 42,
  dealer_partners: 65,
  projects_supported: 280,
  years_experience: 18,
});

// --- Products (Fibaro) ---
let productsCache = null;
export async function fetchProducts() {
  if (productsCache) return productsCache;
  try {
    const rows = await loadSheet("/data/products.xlsx");
    productsCache = rows.map((r) => ({
      name: r.name || r.Name || "",
      category: r.category || r.Category || "",
      tagline: r.tagline || r.Tagline || "",
      body: r.description || r.Description || "",
      features: String(r.features || r.Features || "").split("|").map((s) => s.trim()).filter(Boolean),
      image: `/products/${(r.image_file || r["image_file"] || "").trim()}`,
      link: r.product_url || r["product_url"] || "#",
      hero: truthy(r.featured ?? r.Featured),
    }));
  } catch (e) {
    console.warn("Loading products.xlsx failed:", e);
    productsCache = [];
  }
  return productsCache;
}

// --- Inquiry ---
export const submitInquiry = async (payload) => {
  const body = {
    _subject: `[Jain Interior Solutions] New ${(payload.type || "").toUpperCase()} Enquiry`,
    _template: "table",
    _captcha: "false",
    ...payload,
  };
  const res = await fetch(FORMSUBMIT_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error("Submission failed");
  return { ok: true, id: Date.now().toString() };
};

export const API = "";
export const api = { get: async () => ({ data: [] }), post: async () => ({ data: {} }) };
